'use client';

import React, { useId } from 'react';
import { ParsedHardwareProductVariant } from '@/ui/components/pages/ProductDetail/types/ParsedHardwareProductVariant';
import { ParsedProductVariant } from '@/ui/components/pages/ProductDetail/types/ParsedProductVariant';
import OptimizedImage from '@/ui/components/shared/OptimizedImage';
import { useLocalization } from '@/ui/providers/localizationProvider';
import { BundleProduct } from '@/ui/services/BundleProductService';
import { BundleSlotTileOption, BundleSlotTiles } from './bundle-slot-tiles';

export interface BundleSlotBlockProps {
  product: BundleProduct;
  selectedVariant?: ParsedProductVariant;
  onSelect: (variant: ParsedHardwareProductVariant) => void;
}

interface SlotTiles {
  options: BundleSlotTileOption[];
  selectedKey?: string;
  select: (key: string) => ParsedHardwareProductVariant | undefined;
}

/**
 * Turns the slot's options into tiles: colours are keyed by name and drawn as a
 * square of the colour, variant and cylinder tiles are keyed by SKU and show
 * their image. Options the visitor's country hides are already left out.
 */
function getSlotTiles(
  product: BundleProduct,
  country: string | null | undefined,
  selectedSku?: string,
): SlotTiles | undefined {
  const slotOptions = product.getSlotOptions(country);

  if (slotOptions.kind === 'color') {
    const { colors } = slotOptions;
    return {
      options: colors.map((color) => ({
        key: color.name,
        label: color.name,
        content: (
          <span
            data-testid="bundle-slot-color-square"
            className="h-10 w-10 rounded-lg border border-neutral-15"
            style={{ backgroundColor: color.hex }}
            aria-hidden
          />
        ),
      })),
      selectedKey: selectedSku ? product.getVariantBySKU(selectedSku)?.color?.name : undefined,
      select: (key) => {
        const color = colors.find((option) => option.name === key);
        return color && product.getVariantForOption(color);
      },
    };
  }

  if (slotOptions.kind === 'variant') {
    const { tiles } = slotOptions;
    return {
      options: tiles.map((tile) => ({
        key: tile.type,
        label: tile.name,
        content: (
          <OptimizedImage
            image={tile.image}
            sizes="64px"
            width={64}
            height={64}
            className="h-16 w-16 object-contain"
          />
        ),
      })),
      selectedKey: selectedSku,
      select: (key) => {
        const tile = tiles.find((option) => option.type === key);
        return tile && product.getVariantForOption(tile);
      },
    };
  }

  return undefined;
}

/**
 * One product of the set on the bundle page: its name and, for a slot with a
 * choice, one tile per option. A slot with a single variant shows its name only.
 */
export function BundleSlotBlock({
  product,
  selectedVariant,
  onSelect,
}: Readonly<BundleSlotBlockProps>) {
  const { country } = useLocalization();
  const headingId = useId();
  const shownVariant = selectedVariant ?? product.defaultVariant;
  const slotTiles = product.isSelectable()
    ? getSlotTiles(product, country, shownVariant?.sku)
    : undefined;

  const selectKey = (key: string) => {
    const chosen = slotTiles?.select(key);
    if (chosen) {
      onSelect(chosen);
    }
  };

  return (
    <section
      aria-labelledby={headingId}
      data-testid="classic-pdp-bundle-slot"
      className="flex flex-col gap-4"
    >
      <h3 id={headingId} className="text-xl font-bold">
        {product.name}
      </h3>
      {slotTiles && (
        <BundleSlotTiles
          options={slotTiles.options}
          selectedKey={slotTiles.selectedKey}
          onSelect={selectKey}
          labelledBy={headingId}
        />
      )}
    </section>
  );
}
